'use client'

import '../personal.css'

import type {
  CmsRateRow as RateRow,
  CmsRateSection as RateSection,
  CmsRatesPage as RatesPage
} from '@/types/cms'

import { useEffect, useState } from 'react'
import { Col, Container, Row, Table } from 'react-bootstrap'
// oxlint-disable-next-line no-restricted-imports -- hydration-safe: see ARCHITECTURE.md trap #1
import { useMediaQuery } from 'react-responsive'
import parse from 'html-react-parser'

import downArrow from '@/assets/images/line-arrow-down.svg'
import upArrow from '@/assets/images/line-arrow-up.svg'

/* ================= COMPONENT ================= */

export default function SavingDepositRates({ initialData }: { initialData?: RatesPage }) {
  const isMdUpQuery = useMediaQuery({ minWidth: 768 })
  // useMediaQuery is false during SSR, so the column widths differ between the
  // server and first client render and cause a hydration attribute mismatch.
  // Default to the desktop layout until mounted so both renders agree.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const isMdUp = mounted ? isMdUpQuery : true

  // Static content baked at build time (static export — no runtime refetch).
  const data = initialData

  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({})

  const toggleSection = (id: number) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const tables = data?.table?.[0]?.table_name || []

  return (
    <Container>
      {/* TITLE */}
      <h1 className="heading-h2 pt-5 pb-5">{parse(data?.title || '')}</h1>

      {/* TABLE SECTIONS */}
      {tables.map(section => {
        const rows = section.table_row_data || []

        const latestDate = section.rateUpdatedDate ? new Date(section.rateUpdatedDate) : null

        const expanded = expandedSections[section.id]
        const visibleRows = expanded ? rows : rows.slice(0, 6)

        return (
          <Row key={section.id} className="mb-3">
            <Col xs={12}>
              {/* SECTION TITLE */}
              <h2 className="mb-1 fs-24 fs-22 fw-bold">{parse(section.table_name || '')} </h2>
              {latestDate && (
                <p className="fw-normal text-light-secondary">
                  (Updated as of{' '}
                  {latestDate.toLocaleDateString('en-MY', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                  )
                </p>
              )}

              {/* TABLE */}
              <div className="position-relative mb-md-5 mb-5">
                <Table
                  responsive
                  bordered
                  hover
                  className="mb-0"
                  style={{ tableLayout: 'fixed', width: '100%' }}
                >
                  {/* HEADER */}
                  <thead>
                    <tr>
                      <th
                        style={{ width: isMdUp ? '40%' : undefined }}
                        className="py-3 px-2 text-center bg-primary text-white fs-md-18 fs-16"
                      >
                        {section.column1}
                      </th>
                      <th
                        style={{ width: isMdUp ? '60%' : undefined }}
                        className="py-3 px-2 text-center bg-primary text-white fs-md-18 fs-16"
                      >
                        {section.column2}
                      </th>
                    </tr>
                  </thead>

                  {/* BODY */}
                  <tbody>
                    {visibleRows.map((row, index) => {
                      const isLastRow = index === visibleRows.length - 1

                      return (
                        <tr key={row.id}>
                          <td
                            className="text-center fs-16 fs-14"
                            style={{
                              borderBottom: isLastRow ? 'none' : undefined
                            }}
                          >
                            {row.value}
                          </td>

                          <td
                            className="text-center fs-16 fs-14"
                            style={{
                              borderBottom: isLastRow ? 'none' : undefined
                            }}
                          >
                            {row.rate}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>

                {/* VIEW MORE */}
                {!expanded && rows.length > 5 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: -20,
                      left: 0,
                      width: '100%',
                      padding: '20px 0',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff 70%)',
                      textAlign: 'center'
                    }}
                  >
                    <button
                      onClick={() => toggleSection(section.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontWeight: 'bold',
                        color: '#1a33b8',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer'
                      }}
                    >
                      View more
                      <img src={downArrow.src} alt="expand" />
                    </button>
                  </div>
                )}

                {/* VIEW LESS */}
                {expanded && rows.length > 5 && (
                  <div className="mt-4 mb-4 text-center">
                    <button
                      onClick={() => toggleSection(section.id)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        fontWeight: 'bold',
                        color: '#1a33b8',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer'
                      }}
                    >
                      View less
                      <img src={upArrow.src} alt="collapse" />
                    </button>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )
      })}

      {/* FOOTER → from API */}
      <p className="pt-md-0 fs-md-14 fs-12" style={{ color: '#000045b3' }}>
        {data?.ctaLinkText}
      </p>
    </Container>
  )
}
